import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate } from "react-router-dom";
import { getRun, uploadUserInputCsv, getDataRefreshTimestamp } from "../utils/api";

const POLL_MS = 5000;

/**
 * @param {{
 *   runId: string | null;
 *   onUploadSuccess?: () => void;
 *   onBusinessFrontStaleHoldChange?: (held: boolean) => void;
 *   title?: string;
 * }} props
 * `onBusinessFrontStaleHoldChange(true)` while a queued upload is in flight; Notebook hides recommendation above Pilot Result insights until the pipeline completes or fails.
 */
export default function BusinessDataSourcePanel({
  runId,
  onUploadSuccess,
  onBusinessFrontStaleHoldChange,
  // Optional so Notebook keeps the generic label unchanged. The Live page passes
  // "Your trades", because there the section IS the customer's own trade file —
  // "Data source" named the plumbing rather than the content.
  title = "Data source",
}) {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ kind: "", text: "" });
  const [expectUlpPoll, setExpectUlpPoll] = useState(false);
  const [ulp, setUlp] = useState({
    status: null,
    updatedAt: null,
    error: null,
    summary: null,
    notebookGcs: null,
    executeStatus: null,
    executeError: null,
  });
  const [dataRefreshAt, setDataRefreshAt] = useState(null);
  /** After a new CSV upload, hide stale "Prediction is complete" until combine/execute advances. */
  const [suppressPredictionComplete, setSuppressPredictionComplete] = useState(false);
  const prevUlpStatusRef = useRef(null);
  const prevExecuteStatusRef = useRef(null);

  const refreshUlp = useCallback(async () => {
    if (!runId) return;
    try {
      const run = await getRun(runId);
      setUlp({
        status: run.user_live_prediction_status ?? null,
        updatedAt: run.user_live_prediction_updated_at ?? null,
        error: run.user_live_prediction_error ?? null,
        summary: run.user_live_prediction ?? null,
        notebookGcs: run.user_live_prediction_notebook_gcs ?? null,
        executeStatus: run.user_live_prediction_execute_status ?? null,
        executeError: run.user_live_prediction_execute_error ?? null,
      });
    } catch {
      /* keep last known */
    }
  }, [runId]);

  useEffect(() => {
    prevUlpStatusRef.current = null;
    prevExecuteStatusRef.current = null;
    if (!runId) {
      setUlp({
        status: null,
        updatedAt: null,
        error: null,
        summary: null,
        notebookGcs: null,
        executeStatus: null,
        executeError: null,
      });
      setExpectUlpPoll(false);
      setSuppressPredictionComplete(false);
      onBusinessFrontStaleHoldChange?.(false);
      prevExecuteStatusRef.current = null;
      return;
    }
    refreshUlp();
  }, [runId, refreshUlp, onBusinessFrontStaleHoldChange]);

  const fetchDataRefreshAt = useCallback(() => {
    if (!runId) { setDataRefreshAt(null); return; }
    getDataRefreshTimestamp(runId)
      .then((d) => setDataRefreshAt(d?.timestamp ?? null))
      .catch(() => setDataRefreshAt(null));
  }, [runId]);

  useEffect(() => { fetchDataRefreshAt(); }, [fetchDataRefreshAt]);

  useEffect(() => {
    if (ulp.status === "running" || ulp.status === "failed") {
      setSuppressPredictionComplete(false);
    }
  }, [ulp.status]);

  useEffect(() => {
    if (!runId) return;
    const ulpStatus = ulp.status;
    const summary = ulp.summary && typeof ulp.summary === "object" ? ulp.summary : null;
    const nbInfo =
      summary?.user_live_prediction_notebook && typeof summary.user_live_prediction_notebook === "object"
        ? summary.user_live_prediction_notebook
        : null;
    const hasNotebook = !!(ulp.notebookGcs || nbInfo?.notebook_gcs);
    const notebookSkipped = !!nbInfo?.skipped;
    const running = ulpStatus === "running";
    const executeRunning = ulp.executeStatus === "running";
    const executeDone =
      ulp.executeStatus === "succeeded" || ulp.executeStatus === "failed";
    const awaitingExecute =
      ulpStatus === "succeeded" && hasNotebook && !notebookSkipped && !executeDone;
    const expectCombine = expectUlpPoll && ulpStatus !== "failed";
    const shouldPoll = running || expectCombine || executeRunning || awaitingExecute;
    if (!shouldPoll) return;
    const id = setInterval(() => {
      refreshUlp();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [
    runId,
    ulp.status,
    ulp.executeStatus,
    ulp.summary,
    ulp.notebookGcs,
    expectUlpPoll,
    refreshUlp,
  ]);

  useEffect(() => {
    if (ulp.status === "failed") {
      setExpectUlpPoll(false);
    }
  }, [ulp.status]);

  useEffect(() => {
    const prev = prevUlpStatusRef.current;
    prevUlpStatusRef.current = ulp.status;
    if (prev === "running" && (ulp.status === "succeeded" || ulp.status === "failed")) {
      setExpectUlpPoll(false);
    }
  }, [ulp.status, onUploadSuccess]);

  useEffect(() => {
    const prev = prevExecuteStatusRef.current;
    prevExecuteStatusRef.current = ulp.executeStatus;
    if (prev === "running" && ulp.executeStatus === "succeeded") {
      onUploadSuccess?.();
    }
  }, [ulp.executeStatus, onUploadSuccess]);

  /** Hide the upload toast once in-step progress UI takes over (single message at a time). */
  useEffect(() => {
    const combineProgress =
      uploading ||
      ulp.status === "running" ||
      (expectUlpPoll && ulp.status !== "failed");
    if (combineProgress) {
      setMessage({ kind: "", text: "" });
    }
  }, [ulp.status, expectUlpPoll, uploading]);

  const ulpStatus = ulp.status;
  const summary = ulp.summary && typeof ulp.summary === "object" ? ulp.summary : null;
  const nbInfo = summary?.user_live_prediction_notebook && typeof summary.user_live_prediction_notebook === "object"
    ? summary.user_live_prediction_notebook
    : null;

  const hasNotebook = !!(ulp.notebookGcs || nbInfo?.notebook_gcs);
  const notebookSkipped = !!nbInfo?.skipped;
  const executeStatus = ulp.executeStatus;
  const executeFailed = executeStatus === "failed";

  /** In-flight combine: file uploading, worker running, or waiting for combine after upload. */
  const showCombineProgress =
    uploading ||
    ulpStatus === "running" ||
    (expectUlpPoll && ulpStatus !== "failed");
  const showExecuteProgress =
    ulpStatus === "succeeded" && hasNotebook && !notebookSkipped && executeStatus === "running";
  const predictionPipelineDone =
    ulpStatus === "succeeded" && ((!hasNotebook || notebookSkipped) || executeStatus === "succeeded");
  const showPredictionComplete =
    predictionPipelineDone &&
    !showCombineProgress &&
    !showExecuteProgress &&
    !executeFailed &&
    !suppressPredictionComplete;

  useEffect(() => {
    // Same gate as the green "Prediction is complete" alert — avoids clearing while local `ulp` is stale after upload.
    if (showPredictionComplete) {
      onBusinessFrontStaleHoldChange?.(false);
    }
  }, [showPredictionComplete, onBusinessFrontStaleHoldChange]);

  useEffect(() => {
    if (ulpStatus === "failed") {
      onBusinessFrontStaleHoldChange?.(false);
    }
  }, [ulpStatus, onBusinessFrontStaleHoldChange]);

  useEffect(() => {
    if (ulp.executeStatus === "failed") {
      onBusinessFrontStaleHoldChange?.(false);
    }
  }, [ulp.executeStatus, onBusinessFrontStaleHoldChange]);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !runId) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setMessage({ kind: "error", text: "Please choose a .csv file." });
      return;
    }
    setUploading(true);
    setMessage({ kind: "", text: "" });
    setSuppressPredictionComplete(true);
    try {
      const data = await uploadUserInputCsv(runId, file);
      let text = "Your file was saved for this run.";
      if (data?.user_live_prediction_queued) {
        setExpectUlpPoll(true);
        onBusinessFrontStaleHoldChange?.(true);
        refreshUlp();
      } else {
        onBusinessFrontStaleHoldChange?.(false);
        setSuppressPredictionComplete(false);
        if (data?.user_live_prediction_task_error) {
          setMessage({ kind: "error", text: "Something went wrong. Please contact Assertion AI team for assistance." });
          return;
        }
      }
      setMessage({ kind: "success", text });
    } catch (err) {
      onBusinessFrontStaleHoldChange?.(false);
      setSuppressPredictionComplete(false);
      setMessage({
        kind: "error",
        text: "Something went wrong. Please contact Assertion AI team for assistance.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {dataRefreshAt ? (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {/* "Data as of <Mon D, YYYY, h:mm AM/PM>" — the same wording and the
                  same format the report's own footer uses, so a reader moving down
                  the page isn't re-parsing a new date style per section. No zone
                  suffix: unlike the report (fixed to Central) this renders in the
                  viewer's own timezone, so stamping it "CT" would be wrong. */}
              Data as of{" "}
              {new Date(dataRefreshAt).toLocaleString("en-US", {
                month: "short", day: "numeric", year: "numeric",
                hour: "numeric", minute: "2-digit",
              })}
            </Typography>
          </Box>
        ) : null}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="Data source options"
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 1 } }}
        >
          <Tab label="Upload data" id="business-data-tab-upload" aria-controls="business-data-panel-upload" />
          <Tab label="Connect to database" id="business-data-tab-db" aria-controls="business-data-panel-db" />
        </Tabs>
      </Box>

      <Box
        role="tabpanel"
        hidden={tab !== 0}
        id="business-data-panel-upload"
        aria-labelledby="business-data-tab-upload"
        sx={{ p: 2.5 }}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Upload a spreadsheet (CSV) from your computer to attach your own numbers to this run. Use the first row for
            column names. If you upload again, the new file replaces the last one. After the upload, we save your file
            securely and automatically combine it with the reference data your team already uses, so everything stays in
            sync for analysis and reporting.
          </Typography>

          {runId &&
          (showCombineProgress ||
            showExecuteProgress ||
            showPredictionComplete ||
            ulpStatus === "failed" ||
            (ulpStatus === "succeeded" && executeFailed)) ? (
            <Stack spacing={1.5}>
              {showCombineProgress ? (
                <Box aria-live="polite">
                  <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Processing and preparing your data...
                  </Typography>
                </Box>
              ) : null}

              {showExecuteProgress ? (
                <Box aria-live="polite">
                  <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Running prediction on your data...
                  </Typography>
                </Box>
              ) : null}

              {showPredictionComplete ? (
                <Alert severity="success" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                  <Typography variant="body2" component="span">
                    Prediction is complete. Check the recommended strategy below.
                  </Typography>
                </Alert>
              ) : null}

              {ulpStatus === "failed" ? (
                <Alert severity="error">
                  Something went wrong. Please contact Assertion AI team for assistance.
                </Alert>
              ) : null}

              {ulpStatus === "succeeded" && executeFailed ? (
                <Alert severity="error">
                  Something went wrong. Please contact Assertion AI team for assistance.
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {!runId ? (
            <Alert severity="warning">Open a run to upload data.</Alert>
          ) : null}
          {message.text ? (
            <Alert severity={message.kind === "success" ? "success" : "error"} onClose={() => setMessage({ kind: "", text: "" })}>
              {message.text}
            </Alert>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFile}
          />
          <Box>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handlePick}
              disabled={!runId || uploading}
            >
              {uploading ? "Uploading…" : "Choose CSV file"}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Box
        role="tabpanel"
        hidden={tab !== 1}
        id="business-data-panel-db"
        aria-labelledby="business-data-tab-db"
        sx={{ p: 2.5 }}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Connect a warehouse or database as your data source. This flow is separate from CSV upload; you can use it
            when your data already lives in a connected system.
          </Typography>
          <Button variant="outlined" onClick={() => nav("/dictionaries/import-snowflake")}>
            Go to database connection
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
